import React, { useEffect, useState } from 'react';
import customLampImg from '../assets/custom-lamp.png';
import about1Img from '../assets/about1.png';

// Custom asset icon imports for Brand Value points
import premiumIcon from '../assets/icon/premium.png';
import customIcon from '../assets/icon/custom.png';
import b2bIcon from '../assets/icon/b2b.png';
import deliveryIcon from '../assets/icon/delivery.png';
import moqIcon from '../assets/icon/moq.png';
import supportIcon from '../assets/icon/support.png';

// Custom asset icon imports for Trust & Security matrices
import secureIcon from '../assets/icon/secure.png';
import globalIcon from '../assets/icon/global.png';
import complianceIcon from '../assets/icon/compliance.png';
import { getPublicRequest } from '../api/auth.js';
import API_ROUTES from './config/apiConfig.js';

const coreValues = [
  { icon: premiumIcon, label: "Premium Quality", sub: "Finest materials & superior craftsmanship" },
  { icon: customIcon, label: "Custom Solutions", sub: "Tailored designs for your brand & requirements" },
  { icon: b2bIcon, label: "Competitive Pricing", sub: "Best value for bulk purchases" },
  { icon: deliveryIcon, label: "Timely Delivery", sub: "Reliable logistics across the globe" },
  { icon: moqIcon, label: "MOQ Friendly", sub: "Flexible minimum order quantities" },
  { icon: supportIcon, label: "Dedicated Support", sub: "Personalized service at every step" }
];

const industries = [
  {
    name: "Luxury Hotels & Resorts",
    tagline: "Custom hospitality lighting",
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M5 21V7l8-4v18M13 7h6v14M9 9h1M9 13h1M9 17h1M16 11h1M16 15h1" />
      </svg>
    )
  },
  {
    name: "Restaurants & Cafes",
    tagline: "Warm ambient fixtures",
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8zM6 1v3M10 1v3M14 1v3" />
      </svg>
    )
  },
  {
    name: "Interior Designers",
    tagline: "Bespoke concept pieces",
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="m19 11-8-8-8.5 8.5a2.12 2.12 0 0 0 3 3L7 13v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-6l1.5 1.5a2.12 2.12 0 0 0 3-3zM9 21v-6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v6" />
      </svg>
    )
  },
  {
    name: "Corporate Spaces",
    tagline: "Modern architectural accents",
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <path d="M9 22v-4h6v4M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" />
      </svg>
    )
  },
  {
    name: "High-End Villas",
    tagline: "Statement residential decor",
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 10.5 12 3l9 7.5V20a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9.5zM9 22v-7a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v7" />
      </svg>
    )
  },
  {
    name: "Export & Retail Chains",
    tagline: "Scalable bulk supply",
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a14.5 14.5 0 0 0 0 20M12 2a14.5 14.5 0 0 1 0 20M2 12h20" />
      </svg>
    )
  }
];

export default function Home({ onNavigate }) {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchHomepageProducts()]);
    } catch (error) {
      console.error("Error fetching homepage data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getPublicRequest(API_ROUTES.GET_CATEGORIES);
      const categoryData = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
      setCategories(categoryData);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchHomepageProducts = async () => {
    try {
      const response = await getPublicRequest(
        `${API_ROUTES.GET_PRODUCTS}?isHomepage=true`
      );
      setProducts(response.data?.items || response.data?.data?.items || []);
    } catch (err) {
      console.error("Failed to fetch homepage products:", err);
    }
  };

  return (
    <div className="homepage-master-wrapper">

      {/* 1. CATEGORIES SECTION */}
      <section className="container categories-section-wrapper">
        <div className="section-title-wrapper">
          <div className="title-left-group">
            <h2>Shop By Category</h2>
            <p className="section-subtitle">Exquisite range of lamps crafted for every purpose</p>
          </div>
        </div>

        <div className="category-grid">
          {loading ? (
            [1, 2, 3, 4].map((n) => (
              <div key={n} className="luxury-category-card skeleton-card">
                <div className="card-image-crop skeleton-pulse"></div>
                <div className="card-info">
                  <div className="skeleton-line title-skel skeleton-pulse"></div>
                  <div className="skeleton-line desc-skel skeleton-pulse"></div>
                  <div className="skeleton-line btn-skel skeleton-pulse"></div>
                </div>
              </div>
            ))
          ) : (
            <>
              {categories?.slice(0, 3).map((cat) => (
                <div
                  key={cat._id}
                  className="luxury-category-card"
                  onClick={() =>
                    onNavigate("subcategory", {
                      categorySlug: cat.slug,
                    })
                  }

                >
                  <div className="card-image-crop">
                    <img src={cat.imageUrl} alt={cat.name} loading="lazy" />
                  </div>
                  <div className="card-info">
                    <div className="card-title-row">
                      {cat.icon}
                      <h3>{cat.name}</h3>
                    </div>
                    <p className="card-description">{cat.description}</p>
                    <div className="card-footer-anchor">
                      <span className="explore-link">Explore ➔</span>
                    </div>
                  </div>
                </div>
              ))}

              <div className="luxury-category-card static-custom-card" onClick={() => onNavigate('custom-design')}>
                <div className="card-image-crop">
                  <img src={customLampImg} alt="Custom Designed Lamp" loading="lazy" />
                </div>
                <div className="card-info">
                  <div className="card-title-row">
                    <h3>Custom Designed Lamp</h3>
                  </div>
                  <p className="card-description">
                    Bring your unique vision to life with our bespoke, tailor-made lighting solutions.
                  </p>
                  <div className="card-footer-anchor">
                    <span className="explore-link customize-highlight">Customize ➔</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* 2. WHY PARTNER PANEL */}
      <section className="container partner-section-wrapper">
        <div className="why-partner-box">
          <h2>Why Partner With Lightingale?</h2>
          <p className="partner-subtitle">We deliver more than products – we build lasting business relationships.</p>
          <div className="partner-grid">
            {coreValues.map((val, idx) => (
              <div key={idx} className="partner-card-item">
                <img src={val.icon} alt="" className="partner-value-icon" />
                <h4>{val.label}</h4>
                <p>{val.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. NEW ARRIVALS */}
      <section className="container new-arrivals-section-wrapper">
        <div className="section-title-wrapper">
          <div className="title-left-group">
            <h2>New Arrivals</h2>
            <p className="section-subtitle">Discover our latest handcrafted lamp designs</p>
          </div>
        </div>

        <div className="premium-product-grid">
          {products.map((prod) => (
            <div
              key={prod._id}
              className="luxury-product-card"
              onClick={() => onNavigate("pdp", prod)}
            >
              <div className="product-image-crop">
                {prod.isNew && <span className="new-badge">New</span>}
                <img
                  src={
                    prod.primaryImage?.url ||
                    prod.images?.[0]?.url ||
                    "/images/no-image.png"
                  }
                  alt={prod.title}
                  loading="lazy"
                />
              </div>

              <div className="product-card-info">
                <h4>{prod.title}</h4>
                <p className="product-sku-code">
                  {prod.productCode || ""}
                </p>
                <span className="enquire-now-link">
                  Enquire Now ➔
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. STATS STRIPE */}
      <section className="container global-stats-stripe-wrapper">
        <div className="stats-grid-layout">
          <div className="stat-box-node"><h3>10+</h3><p>Years of Excellence</p></div>
          <div className="stat-box-node"><h3>1000+</h3><p>Products</p></div>
          <div className="stat-box-node"><h3>500+</h3><p>B2B Partners</p></div>
          <div className="stat-box-node"><h3>50+</h3><p>Countries Served</p></div>
          <div className="stat-box-node"><h3>98%</h3><p>Client Satisfaction</p></div>
          <div className="stat-box-node"><h3>24/7</h3><p>Business Support</p></div>
        </div>
      </section>

      {/* 5. REDESIGNED INDUSTRIES WE SERVE */}
      <section className="container industry-showcase-wrapper">
        <div className="industry-header-box">
          <span className="industry-tag">Commercial & B2B Solutions</span>
          <h2>Industries We Serve</h2>
          <p className="industry-subtitle">Trusted by businesses and design partners across the globe</p>
        </div>

        <div className="industry-flex-grid">
          {industries.map((ind, i) => (
            <div key={i} className="industry-showcase-card">
              <div className="industry-icon-wrapper">
                {ind.icon}
              </div>
              <div className="industry-card-content">
                <h4>{ind.name}</h4>
                <p>{ind.tagline}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 6. BOTTOM THREE-COLUMN CTA GRID ARCHITECTURE */}
      <section className="container global-cta-split-matrix-wrapper">
        <div className="cta-matrix-box standard-text-box">
          <h3>Trusted Worldwide</h3>
          <p>Exporting premium quality lamps to 50+ countries with love & trust.</p>
          <div className="trust-badges-horizontal-row">
            <img src={secureIcon} alt="ISO Certified" />
            <img src={globalIcon} alt="CE Standards" />
            <img src={complianceIcon} alt="Made In India Authentic" />
          </div>
        </div>

        <div className="cta-matrix-box custom-dark-green-box">
          <h3>Let's Create Something Beautiful Together</h3>
          <p>Get the best wholesale prices and custom solutions for your business needs.</p>
          <div className="cta-button-group-row">
            <button className="btn-solid-wholesale-white" onClick={() => onNavigate('contact')}>Bulk Enquiry</button>
            <a href="tel:+919350824043" className="cta-phone-anchor">+91 9350824043</a>
          </div>
        </div>

        <div className="cta-matrix-box custom-amber-gold-box">
          <div className="gold-box-content-left">
            <h3>Need Custom Lamps?</h3>
            <p>Share your ideas and our team will turn them into reality.</p>
            <button className="btn-solid-dark-gold" onClick={() => onNavigate('custom-design')}>Request Custom Quote</button>
          </div>
          <div className="gold-box-image-right">
            <img src={about1Img} alt="Custom sample blueprint lamp" />
          </div>
        </div>
      </section>

    </div>
  );
}